import { useState, useEffect } from "react";
import api from "../../axiosconfig";
import { monthKeyToRange, monthKeyToLabel } from "../../utils/dateUtils";

/**
 * Hook to fetch and compare assessment averages across two date ranges
 * 
 * @param {string} viewMode - "current" or "range"
 * @param {string} startKey - Start month key (e.g., "2026-01")
 * @param {string} endKey - End month key (e.g., "2026-12")
 * @param {object} filters - Filter object
 * 
 * @returns {object} {
 *   prevMetrics, prevData, prevLabel,
 *   focusMetrics, focusData, focusLabel,
 *   loading, error,
 *   hasPrevData, hasFocusData
 * }
 */
export default function useAssessmentRange(viewMode, startKey, endKey, filters) {
    const [prevMetrics, setPrevMetrics] = useState(null);
    const [prevData, setPrevData] = useState([]);
    
    const [focusMetrics, setFocusMetrics] = useState(null);
    const [focusData, setFocusData] = useState([]);
    
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Auto label for UI
    const prevLabel = monthKeyToLabel(startKey);
    const focusLabel = monthKeyToLabel(endKey);

    useEffect(() => {
        // Only fetch in range mode
        if (viewMode !== "range") return;
        if (!startKey || !endKey) return;

        const fetchRangeAverages = async () => {
            try {
                setLoading(true);
                setError(null);

                // Get date ranges from month keys
                const prev = monthKeyToRange(startKey);
                const focus = monthKeyToRange(endKey);

                if (!prev || !focus) {
                    setError("Invalid date range");
                    return;
                }

                // Build params for both requests
                const buildParams = (start, end) => {
                    const params = {
                        range_start_month: start,
                        range_end_month: end,
                    };

                    // Add filters
                    if (filters.age) params.age = filters.age;
                    if (filters.gender) params.gender = filters.gender;
                    if (filters.location) params.location = filters.location;
                    if (filters.description) params.description = filters.description;
                    if (filters.jobTitle) params.jobTitle = filters.jobTitle;
                    if (filters.company) params.company = filters.company;
                    if (filters.interest) params.interest = filters.interest;

                    return params;
                };

                // Fetch both ranges in parallel
                const [prevRes, focusRes] = await Promise.all([
                    api.get('/api/admin/assessment-analytics', { 
                        params: buildParams(prev.start, prev.end) 
                    }),
                    api.get('/api/admin/assessment-analytics', { 
                        params: buildParams(focus.start, focus.end) 
                    }),
                ]);

                // Extract data from responses
                const prevPayload = prevRes?.data || {};
                const focusPayload = focusRes?.data || {};

                // Extract averages from nested structure
                const prevAverages = {
                    physical: prevPayload.averages?.physical || 0,
                    emotional: prevPayload.averages?.emotional || 0,
                    acceptance: prevPayload.averages?.acceptance || 0,
                    relationships: prevPayload.averages?.relationships || 0,
                    loving_kindness: prevPayload.averages?.loving_kindness || 0,
                    intellectual: prevPayload.averages?.intellectual || 0,
                };

                const focusAverages = {
                    physical: focusPayload.averages?.physical || 0,
                    emotional: focusPayload.averages?.emotional || 0,
                    acceptance: focusPayload.averages?.acceptance || 0,
                    relationships: focusPayload.averages?.relationships || 0,
                    loving_kindness: focusPayload.averages?.loving_kindness || 0,
                    intellectual: focusPayload.averages?.intellectual || 0,
                };

                setPrevMetrics(prevAverages);
                setPrevData(prevPayload.data || []);

                setFocusMetrics(focusAverages);
                setFocusData(focusPayload.data || []);

            } catch (e) {
                console.error("[useAssessmentRange] Error fetching range data:", e);
                
                if (e.response?.status === 400) {
                    setError("Invalid filter parameters. Please check your filters.");
                } else if (!navigator.onLine) {
                    setError("Network error. Please check your connection.");
                } else {
                    setError("Unable to load comparison data. Please try again.");
                }

                setPrevMetrics(null);
                setPrevData([]);
                setFocusMetrics(null);
                setFocusData([]);
            } finally {
                setLoading(false);
            }
        };

        fetchRangeAverages();
    }, [viewMode, startKey, endKey, filters.age, filters.gender, filters.location,
        filters.description, filters.jobTitle, filters.company, filters.interest]);

    // Determine if we have valid data for each period
    // Check if at least one domain has a score > 0
    const hasPrevData = 
        prevMetrics !== null && 
        Object.values(prevMetrics).some(v => Number(v) > 0);
    
    const hasFocusData = 
        focusMetrics !== null && 
        Object.values(focusMetrics).some(v => Number(v) > 0);

    return {
        prevMetrics,
        prevData,
        prevLabel,
        focusMetrics,
        focusData,
        focusLabel,
        loading,
        error,
        hasPrevData,
        hasFocusData,
    };
}
